import React, { useState, FunctionComponent } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import {
  greetingDefaultMessage,
  candidateRequestDefaultMessage,
  greetQuestionDefaultMessage,
  welcomeInfo1,
  welcomeInfo2,
  privacyContentLabel,
  privacyContent1,
  privacyContent2,
  termsOfUse1,
  termsOfUse2,
  not as notStr,
  declineQuestion1,
  declinePolicy1,
  declinePolicy2,
  declinePolicy3,
  declinePolicy4,
  termsLink
} from './utils';
import { IBindingAction, IBindingCallback1 } from '../../../../common/models/callback';
import { IAppState } from '../../../../common/models/store/IAppState';
import RegularButton from '../../../../components/RegularButton';
import LinkButton from '../../../../components/LinkButton';
import Icon from '../../../../components/Icon';
import Checkbox from '../../../../components/Checkbox';
import IconButton from '../../../../components/IconButton';
import { IntroPages } from '../Survey/utils';
import { declineSurveyRoutine } from '../../routines';
import SecondDecline from '../../components/SecondDecline';
import styles from './styles.module.scss';
import { useSwipeable } from 'react-swipeable';

interface IIntroProps {
  id: string;
  candidateFirstName: string;
  candidateLastName: string;
  refFirstName: string;
  companyName: string;
  currentPage: IntroPages;
  onContinue: IBindingAction;
  onRejectSurvey: IBindingAction;
  setCurrentPage: (page: IntroPages, goBack?: boolean) => void;
  declineSurvey: IBindingCallback1<string>;
}

const Intro: FunctionComponent<IIntroProps> = ({
  id,
  candidateFirstName,
  candidateLastName,
  refFirstName,
  companyName,
  currentPage,
  onRejectSurvey,
  declineSurvey,
  setCurrentPage,
  onContinue
}) => {
  const [privacyAccept, setPrivacyAccept] = useState(false);

  const arrowRight = <Icon icon="arrowRight" />;
  const not = (
    <span className="font-weight-bold">
      <FormattedMessage
        id="survey.not"
        defaultMessage={notStr}
      />
    </span>
  );

  const Welcome = () => {
    const swipeHandler = useSwipeable({
      onSwipedLeft: () => setCurrentPage(IntroPages.ContentWaiver),
      trackMouse: false
    });

    return (
      <div {...swipeHandler} className="survey-page">
        <div className="d-flex align-items-center flex-column survey-page-wrapper">
          <p className="survey-page-title text-center">
            <FormattedMessage
              id="survey.greeting"
              defaultMessage={greetingDefaultMessage}
              values={{
                firstName: refFirstName
              }}
            />
          </p>
          <p className={styles.subtitle}>
            <FormattedMessage
              id="survey.candidateRequest"
              defaultMessage={candidateRequestDefaultMessage}
              values={{
                firstName: candidateFirstName,
                lastName: candidateLastName
              }}
            />
          </p>
          <div className="greet-intro">
            <p className="survey-page-text text-center">
              <FormattedMessage
                id="survey.greetQuestion"
                defaultMessage={greetQuestionDefaultMessage}
                values={{
                  firstName: candidateFirstName
                }}
              />
              <br />
              <FormattedMessage
                id="survey.welcomeInfo1"
                defaultMessage={welcomeInfo1}
              />
              {not}
              <FormattedMessage
                id="survey.welcomeInfo2"
                defaultMessage={welcomeInfo2}
                values={{
                  firstName: candidateFirstName
                }}
              />
            </p>
          </div>
          <div className="mb-4">
            <RegularButton
              textIntlId="survey.confirmButton1"
              defaultMessage="Yes, get started!"
              variant="primary"
              size="lg"
              onClick={() => setCurrentPage(IntroPages.ContentWaiver)}
              icon={arrowRight}
              iconPosition="right"
            />
          </div>
          <LinkButton
            textIntlId="survey.declineButton1"
            defaultMessage="No, I decline to answer"
            variant="secondary"
            onClick={() => setCurrentPage(IntroPages.FirstDecline)}
          />
        </div>
      </div>
    );
  };

  const PrivacyContent = () => {
    const swipeHandler = useSwipeable({
      onSwipedLeft: () => {
        if (privacyAccept) onContinue();
      },
      onSwipedRight: () => setCurrentPage(IntroPages.WelcomeScreen, true),
      trackMouse: false
    });

    return (
      <div {...swipeHandler} className="survey-page">
        <div className="survey-page-wrapper">
          <p className="survey-page-title">
            <FormattedMessage
              id="survey.privacyConsentLabel"
              defaultMessage={privacyContentLabel}
            />
          </p>
          <span className={styles.smallLineHeight}>
            <FormattedMessage
              id="survey.privacyConsent1"
              defaultMessage={privacyContent1}
              values={{
                candidate: `${candidateFirstName} ${candidateLastName}`,
                customer: companyName,
                breakingLine: <br />
              }}
            />
          </span>
          <span className={`${styles.smallLineHeight} font-weight-bold`}>
            <FormattedMessage
              id="survey.privacyConsent2"
              defaultMessage={privacyContent2}
              values={{
                candidate: `${candidateFirstName} ${candidateLastName}`,
                customer: companyName,
                breakingLine: <br />
              }}
            />
          </span>
          <div
            role="presentation"
            className={`d-flex flex-row align-items-center ${styles.checkbox}`}
            onClick={() => setPrivacyAccept(!privacyAccept)}
          >
            <Checkbox checked={privacyAccept} />
            <span className={styles.checkboxLabel}>
              <FormattedMessage id="survey.termsOfUse1" defaultMessage={termsOfUse1} />
              &nbsp;
              <a
                className="link inter-font"
                onClick={e => e.stopPropagation()}
                // eslint-disable-next-line react/jsx-no-target-blank
                target="_blank"
                href={termsLink}
              >
                <FormattedMessage
                  id="survey.termsOfUse2"
                  defaultMessage={termsOfUse2}
                />
              </a>
              .
            </span>
          </div>
          <div className={styles.nextButton}>
            <RegularButton
              disabled={!privacyAccept}
              textIntlId="survey.next"
              defaultMessage="next"
              onClick={onContinue}
              icon={arrowRight}
              iconPosition="right"
              size="lg"
            />
          </div>
        </div>
      </div>
    );
  };

  const FirstDecline = () => {
    const swipeHandler = useSwipeable({
      onSwipedLeft: () => setCurrentPage(IntroPages.ContentWaiver),
      onSwipedRight: () => setCurrentPage(IntroPages.WelcomeScreen, true),
      trackMouse: false
    });

    return (
      <div {...swipeHandler} className="survey-page">
        <div className="survey-page-wrapper">
          <p className="survey-page-title">
            <FormattedMessage
              id="survey.declineQuestion1"
              defaultMessage={declineQuestion1}
            />
          </p>
          <span className={styles.smallLineHeight}>
            <FormattedMessage
              id="survey.declinePolicy1"
              defaultMessage={declinePolicy1}
            />
            <span className="font-weight-bold">
              <FormattedMessage
                id="survey.declinePolicy2"
                defaultMessage={declinePolicy2}
              />
            </span>
            <FormattedMessage
              id="survey.declinePolicy3"
              defaultMessage={declinePolicy3}
              values={{
                breakingLine: <br />
              }}
            />
            {not}
            <FormattedMessage
              id="survey.declinePolicy4"
              defaultMessage={declinePolicy4}
              values={{
                breakingLine: <br />
              }}
            />
          </span>
          <div className={styles.declineButtons}>
            <RegularButton
              textIntlId="survey.declineButton2"
              defaultMessage="I decline to answer"
              size="lg"
              variant="tertiary"
              onClick={() => setCurrentPage(IntroPages.SecondDecline)}
            />
            <RegularButton
              textIntlId="survey.confirmButton2"
              defaultMessage="Ok, get started!"
              variant="primary"
              size="lg"
              onClick={() => setCurrentPage(IntroPages.ContentWaiver)}
              icon={arrowRight}
              iconPosition="right"
            />
          </div>
        </div>
      </div>
    );
  };

  const getCurrentPage = () => {
    switch (currentPage) {
      case IntroPages.WelcomeScreen: return <Welcome />;
      case IntroPages.ContentWaiver: return <PrivacyContent />;
      case IntroPages.FirstDecline: return <FirstDecline />;
      case IntroPages.SecondDecline: return (
        <SecondDecline
          declineSurvey={declineSurvey}
          onRejectSurvey={onRejectSurvey}
          onGoBack={getBackbuttonClick}
        />
      );
      default: return null;
    }
  };

  const getBackbuttonClick = () => {
    if (currentPage === IntroPages.SecondDecline) {
      return setCurrentPage(IntroPages.FirstDecline, true);
    }
    return setCurrentPage(IntroPages.WelcomeScreen, true);
  };

  return (
    <>
      {id && getCurrentPage()}
      {
        currentPage !== IntroPages.WelcomeScreen ? (
          <div className="survey-back-bttn">
            <IconButton
              onClick={() => getBackbuttonClick()}
              icon={<Icon icon="arrowLeft" className="backButtonIcon" />}
              variant="bordered"
            />
          </div>
        ) : null
      }
    </>
  );
};

const mapStateToProps = ({
  refSurvey: {
    candidate: { firstName, lastName },
    reference
  },
  workspace: { name }
}: IAppState) => ({
  id: reference.id,
  candidateFirstName: firstName,
  candidateLastName: lastName,
  refFirstName: reference.firstName,
  companyName: name
});

const mapDispatchToProps = {
  declineSurvey: declineSurveyRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(Intro);
